import { useCallback, useMemo, useRef } from 'react';
import { Again, Factors, Passkeys } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { useConfirm } from '@beyond-js/ui/react';
import { useQuestions } from './questions';

/** How confirming ended: proved, not proved, or a window the browser would not open (a press can open it) */
export type Reauthenticated = boolean | 'blocked';

/**
 * Asks the person to prove it is them again, in place, before a sensitive action is sent again (E8:
 * re-authentication for sensitive actions): a passkey in this browser when the account has one, else
 * the password, else an authenticator code, in the shared question over the page. An account with none of
 * them signs in again with its own provider in a window (`Again`), which renews this same session, so the
 * page keeps what the person was doing.
 *
 * A browser opens that window only from the person's press, with nothing awaited first. So the ways to prove it are
 * read ahead (`prime`, when a page already knows it will ask), and a press then opens the window at once. When they
 * were not read yet the window may be blocked: the answer is `blocked`, and the page offers a press that opens it.
 * A proof that arrives after the wait gave up is given to `late`, and the page goes on only when the person presses.
 */
export function useReauthentication({ late }: { late?: () => void } = {}) {
	const { client, text } = usePresence();
	const { prompt } = useConfirm(useQuestions());
	const factors = useMemo(() => new Factors(client), [client]);
	const known = useRef<string[]>();
	const reading = useRef<Promise<string[]>>();
	const after = useRef(late);
	after.current = late;

	const read = useCallback(() => {
		reading.current ??= factors.methods().then(
			methods => (known.current = methods),
			error => {
				reading.current = undefined;
				throw error;
			}
		);
		return reading.current;
	}, [factors]);

	/** Reads the ways to prove it ahead, so a press later opens a window at once */
	const prime = useCallback(() => void read().catch(() => undefined), [read]);

	const trip = useCallback(async (): Promise<Reauthenticated> => {
		const outcome = await new Again().prove(() => after.current?.());
		return outcome === 'proved' ? true : outcome === 'blocked' ? 'blocked' : false;
	}, []);

	const reauthenticate = useCallback(async (): Promise<Reauthenticated> => {
		// Read ahead and only a fresh sign-in applies: the window opens now, inside the press
		const ready = known.current;
		if (ready && Again.only(ready, Passkeys.supported)) return trip();
		let methods: string[];
		try {
			methods = ready ?? (await read());
		} catch {
			return false;
		}
		if (methods.includes('passkey') && Passkeys.supported) {
			try {
				await factors.reauthenticate('passkey');
				return true;
			} catch {
				// Cancelled or not available here: another way follows
			}
		}
		const method = methods.includes('password') ? 'password' : methods.includes('totp') ? 'totp' : undefined;
		if (!method) return methods.includes('provider') ? trip() : false;
		const value = await prompt({
			title: text('reauth.title'),
			message: text(`reauth.${method}`),
			label: text(`reauth.${method}.label`),
			type: method === 'password' ? 'password' : 'text',
			required: true,
			accept: text('reauth.accept'),
			cancel: text('action.cancel'),
			work: (given: unknown) => factors.reauthenticate(method, String(given)),
			explain: (error: unknown) => (Refusal.is(error) ? (error.code === 'FACTOR_INVALID' ? text(`reauth.${method}.wrong`) : text(error.key, error.values)) : text('error.unexpected'))
		});
		return value !== null;
	}, [factors, prompt, read, text, trip]);

	return { reauthenticate, prime };
}

/**
 *     const reauthenticate = useReauthenticate();
 *     if (Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate())) return send();
 *
 * @returns Whether the person proved it, so the action may be sent again; `false` when they cancelled or
 * nothing in place is possible, and the screen then offers signing in again as before
 */
export function useReauthenticate(): () => Promise<boolean> {
	const { reauthenticate } = useReauthentication();
	return useCallback(async () => (await reauthenticate()) === true, [reauthenticate]);
}
